import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { GeneratorPopup, type GeneratorPopupMode } from '@features/tools/components/GeneratorPopup'
import { FieldMarker } from '@shared/ui/FieldMarker'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { TotpRing } from '@shared/ui/TotpRing'
import { useToast } from '@shared/ui/useToast'
import { copyToClipboard } from '@services/clipboard'
import { Icon, type IconName } from '@/icons/Icon'
import { useTranslation } from '@/i18n'
import { useTotp } from '../hooks/useTotp'
import type { UseVaultItemForm } from '../hooks/useVaultItemForm'
import { scorePasswordHeuristic } from '../services/passwordHeuristic'
import type { Folder, UriMatchType, VaultCustomFieldType, VaultItemType } from '../types'

/**
 * Vault item の作成・編集フォーム共通 view (F4 で抽出)。
 *
 * VaultNewPage と VaultEditPage の両方から再利用する presentational component。
 * 状態管理は `useVaultItemForm()` の戻り値を props で受け取り、本 component は
 * type picker / 各 type 固有 field / common field を描画するのみ。
 *
 * 親 page は PageHead を持ち、cancel / save / delete などのアクションボタンは
 * `footer` prop でフォーム末尾 (区切り線付きの `.vault-form-actions`) に差し込む。
 */

const TYPE_PICKER: readonly { key: VaultItemType; icon: IconName }[] = [
  { key: 'login', icon: 'globe' },
  { key: 'card', icon: 'credit-card' },
  { key: 'note', icon: 'file-text' },
  { key: 'identity', icon: 'id-card' },
]

/** 6 / 8 桁コードを中央で 2 分割して可読性を上げる (VaultDetailPage の formatTotpCode と同等)。 */
const formatTotpCode = (code: string): string => {
  if (code.length <= 4) return code
  const mid = Math.floor(code.length / 2)

  return `${code.slice(0, mid)} ${code.slice(mid)}`
}

/**
 * フォーム上の TOTP secret 入力欄直下に live OTP プレビューを描画する
 * (Bitwarden / 1Password の vault 編集画面と同パターン)。
 *
 * - 空入力 → null (何も描画しない)
 * - 有効な Base32 → 現在のコード + TotpRing (残り秒数 / progress) + コピーボタン
 * - 無効な Base32 → 小さなインライン error メッセージ
 *
 * Detail 画面の TotpRow と同じ `useTotp` / `TotpRing` を共有する。
 */
const TotpFormPreview = ({ secret }: { secret: string }): JSX.Element | null => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const state = useTotp(secret)

  if (secret.trim() === '') return null

  const onCopy = async (): Promise<void> => {
    if (state.kind !== 'active') return
    try {
      await copyToClipboard(state.code)
      showToast({
        titleKey: 'vault.detail.toast.copied_totp_code',
        tone: 'success',
        dedupe: true,
      })
    } catch {
      showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
    }
  }

  return (
    <div className="totp-form-preview" aria-live="polite">
      <span className="totp-form-preview-label">{t('vault.new.totp.preview_label')}</span>
      {state.kind === 'active' && (
        <>
          <span className="totp-form-preview-code mono">{formatTotpCode(state.code)}</span>
          <TotpRing progress={state.progress} remainingMs={state.remainingMs} />
          <button
            type="button"
            className="icon-btn sm"
            onClick={() => void onCopy()}
            aria-label={t('vault.detail.action.copy_totp_code')}
            title={t('vault.detail.action.copy_totp_code')}
          >
            <Icon name="copy" />
          </button>
        </>
      )}
      {state.kind === 'computing' && <span className="totp-form-preview-code mono">— — —</span>}
      {state.kind === 'error' && (
        <span className="totp-form-preview-error">{t(state.messageKey)}</span>
      )}
    </div>
  )
}

export interface VaultItemFormViewProps {
  form: UseVaultItemForm
  folders: readonly Folder[]
  formId: string
  /** フォーム末尾の区切り線下に並べるアクションボタン (cancel / save / delete 等)。 */
  footer?: ReactNode
}

export const VaultItemFormView = ({
  form,
  folders,
  formId,
  footer,
}: VaultItemFormViewProps): JSX.Element => {
  const { t } = useTranslation()
  const folderSelectId = useId()
  const nameId = useId()
  const notesId = useId()
  const usernameId = useId()
  const passwordId = useId()
  const totpId = useId()
  const cardNumberId = useId()
  const cardHolderId = useId()
  const cardExpId = useId()
  const cardCvvId = useId()
  const bodyId = useId()
  // TASK-WEB-004: identity form の input id (新 7 fields)。
  const idNumberId = useId()
  const fullNameId = useId()
  const genderId = useId()
  const dateOfBirthId = useId()
  const addressId = useId()
  const phoneId = useId()
  const emailId = useId()
  const [isPwVisible, setIsPwVisible] = useState(false)
  // #5: 入力欄の生成アイコンから開く生成ポップアップ。null = 非表示。
  const [generatorPopup, setGeneratorPopup] = useState<GeneratorPopupMode | null>(null)

  // パスワード強度メータ (login のみ、軽量ヒューリスティック)。
  const pwScore = useMemo(
    () => scorePasswordHeuristic(form.fields.login.password),
    [form.fields.login.password],
  )

  useEffect(() => {
    if (form.type !== 'login') setIsPwVisible(false)
  }, [form.type])

  const isError = form.state.kind === 'error'
  const errorKey = form.state.kind === 'error' ? form.state.messageKey : null

  // バリデーションエラー発生時に最上部のエラー Notice までスクロールしてフォーカスを当てる。
  // 長いフォームで送信ボタンが画面外にあると、ユーザーは何故送信できないか分からない。
  const errorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (form.state.kind === 'error' && errorRef.current !== null) {
      // jsdom には scrollIntoView が無いので feature-detect (本番ブラウザでは常に有)。
      if (typeof errorRef.current.scrollIntoView === 'function') {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      errorRef.current.focus()
    }
  }, [form.state])

  return (
    <form id={formId} className="card vault-new-card" onSubmit={form.onSubmit} noValidate>
      <div className="vault-new-body">
        {isError && errorKey && (
          <div
            className="vault-new-error vault-new-error-top"
            role="alert"
            ref={errorRef}
            tabIndex={-1}
          >
            <Notice tone="danger" ico="alert">
              <strong>{t('vault.new.error.alert_heading')}</strong>
              <div className="vault-new-error-msg">{t(errorKey)}</div>
            </Notice>
          </div>
        )}
        <div className="field">
          <label className="field-label">{t('vault.new.field.type')}</label>
          <div
            className="vault-type-picker"
            role="radiogroup"
            aria-label={t('vault.new.field.type')}
          >
            {TYPE_PICKER.map(t2 => (
              <button
                key={t2.key}
                type="button"
                role="radio"
                aria-checked={form.type === t2.key}
                className={`vault-type-picker-item${form.type === t2.key ? ' active' : ''}`}
                onClick={() => form.setType(t2.key)}
              >
                <Icon name={t2.icon} aria-hidden="true" />
                <span>{t(`vault.type.${t2.key}`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor={nameId}>
            {t('vault.new.field.name')}
            <FieldMarker required />
          </label>
          <input
            id={nameId}
            className="input lg"
            type="text"
            value={form.fields.common.name}
            onChange={ev => form.setCommon('name', ev.target.value)}
            placeholder={t('vault.new.placeholder.name')}
            autoComplete="off"
            required
          />
        </div>

        {/* URL section: 全 type 共通 (login だけでなく card / note / identity でも
            関連 URL を保存できる)。login type では autofill 用、それ以外は参考リンク。
            複数 URI + 一致検出方式 (match) の UI は全 type で同じ。 */}
        <div className="field">
          <label className="field-label">
            {t('vault.new.field.url')}
            <FieldMarker />
          </label>
          {form.fields.common.uris.map((u, i) => (
            <div key={i} className="vault-uri-row">
              <input
                className="input"
                type="url"
                value={u.uri}
                onChange={ev => form.updateUri(i, { uri: ev.target.value })}
                placeholder="https://example.com"
                autoComplete="off"
              />
              <select
                className="select vault-uri-match"
                value={u.match ?? ''}
                aria-label={t('vault.new.field.uri_match')}
                onChange={ev =>
                  form.updateUri(i, {
                    match:
                      ev.target.value === '' ? undefined : (ev.target.value as UriMatchType),
                  })
                }
              >
                <option value="">{t('vault.new.uri_match.default')}</option>
                <option value="base_domain">{t('vault.new.uri_match.base_domain')}</option>
                <option value="host">{t('vault.new.uri_match.host')}</option>
                <option value="exact">{t('vault.new.uri_match.exact')}</option>
                <option value="starts_with">{t('vault.new.uri_match.starts_with')}</option>
                <option value="regex">{t('vault.new.uri_match.regex')}</option>
                <option value="never">{t('vault.new.uri_match.never')}</option>
              </select>
              <button
                type="button"
                className="icon-btn"
                onClick={() => form.removeUri(i)}
                aria-label={t('vault.new.action.remove_uri')}
              >
                <Icon name="trash" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm vault-list-add-btn"
            onClick={form.addUri}
          >
            <Icon name="plus" aria-hidden="true" />
            {t('vault.new.action.add_uri')}
          </button>
        </div>

        {form.type === 'login' && (
          <>
            <div className="field">
              <label className="field-label" htmlFor={usernameId}>
                {t('vault.new.field.username')}
                <FieldMarker />
              </label>
              <div className="input-group">
                <input
                  id={usernameId}
                  className="input"
                  type="text"
                  value={form.fields.login.username}
                  onChange={ev => form.setLogin('username', ev.target.value)}
                  autoComplete="off"
                />
                <div className="input-suffix">
                  <button
                    type="button"
                    className="icon-btn sm"
                    onClick={() => setGeneratorPopup('username')}
                    aria-label={t('vault.new.action.generate_username')}
                    title={t('vault.new.action.generate_username')}
                    tabIndex={-1}
                  >
                    <Icon name="refresh" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
            <div className="field">
              <label className="field-label" htmlFor={passwordId}>
                {t('vault.new.field.password')}
                <FieldMarker />
              </label>
              <PasswordInput
                id={passwordId}
                value={form.fields.login.password}
                onChange={ev => form.setLogin('password', ev.target.value)}
                autoComplete="new-password"
                visible={isPwVisible}
                onVisibleChange={setIsPwVisible}
                extraAction={
                  <button
                    type="button"
                    className="icon-btn sm"
                    onClick={() => setGeneratorPopup('password')}
                    aria-label={t('vault.new.action.generate_password')}
                    title={t('vault.new.action.generate_password')}
                    tabIndex={-1}
                  >
                    <Icon name="refresh" aria-hidden="true" />
                  </button>
                }
              />
              <div className="strength-row">
                <div className={`strength s${pwScore}`} aria-hidden="true">
                  <div className="seg" />
                  <div className="seg" />
                  <div className="seg" />
                  <div className="seg" />
                </div>
                <span className="strength-label" aria-live="polite">
                  {t('vault.new.strength.label')}: {t(`vault.new.strength.score_${pwScore}`)}
                </span>
              </div>
              <div className="field-help">{t('vault.new.strength.help')}</div>
            </div>
            <div className="field">
              <label className="field-label" htmlFor={totpId}>
                {t('vault.new.field.totp_secret')}
                <FieldMarker />
              </label>
              <input
                id={totpId}
                className="input mono"
                type="text"
                value={form.fields.login.totpSecret}
                onChange={ev => form.setLogin('totpSecret', ev.target.value)}
                placeholder={t('vault.new.placeholder.totp_secret')}
                autoComplete="off"
              />
              <TotpFormPreview secret={form.fields.login.totpSecret} />
            </div>
          </>
        )}

        {form.type === 'card' && (
          <>
            <div className="field">
              <label className="field-label" htmlFor={cardNumberId}>
                {t('vault.new.field.card_number')}
                <FieldMarker />
              </label>
              <input
                id={cardNumberId}
                className="input mono"
                type="text"
                value={form.fields.card.cardNumber}
                onChange={ev => form.setCard('cardNumber', ev.target.value)}
                autoComplete="off"
                inputMode="numeric"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={cardHolderId}>
                {t('vault.new.field.card_holder')}
                <FieldMarker />
              </label>
              <input
                id={cardHolderId}
                className="input"
                type="text"
                value={form.fields.card.cardHolder}
                onChange={ev => form.setCard('cardHolder', ev.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="vault-new-grid-2">
              <div className="field">
                <label className="field-label" htmlFor={cardExpId}>
                  {t('vault.new.field.card_expiry')}
                  <FieldMarker />
                </label>
                <input
                  id={cardExpId}
                  className="input mono"
                  type="text"
                  value={form.fields.card.cardExpiry}
                  onChange={ev => form.setCard('cardExpiry', ev.target.value)}
                  placeholder="MM/YY"
                  autoComplete="off"
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor={cardCvvId}>
                  {t('vault.new.field.card_cvv')}
                  <FieldMarker />
                </label>
                <PasswordInput
                  id={cardCvvId}
                  className="mono"
                  value={form.fields.card.cardCvv}
                  onChange={ev => form.setCard('cardCvv', ev.target.value)}
                  autoComplete="off"
                  inputMode="numeric"
                />
              </div>
            </div>
          </>
        )}

        {form.type === 'note' && (
          <div className="field">
            <label className="field-label" htmlFor={bodyId}>
              {t('vault.new.field.body')}
              <FieldMarker />
            </label>
            <textarea
              id={bodyId}
              className="textarea"
              value={form.fields.note.body}
              onChange={ev => form.setNote('body', ev.target.value)}
              rows={8}
            />
          </div>
        )}

        {form.type === 'identity' && (
          <>
            {/* TASK-WEB-004: 表示順 ID番号 → 氏名 → 性別 → 生年月日 → 住所 → 電話 → メール。
                旧 名/姓 inputs は廃止 (schema は backward compat で保持)。 */}
            <div className="field">
              <label className="field-label" htmlFor={idNumberId}>
                {t('vault.new.field.id_number')}
                <FieldMarker />
              </label>
              <input
                id={idNumberId}
                className="input"
                type="text"
                value={form.fields.identity.idNumber}
                onChange={ev => form.setIdentity('idNumber', ev.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={fullNameId}>
                {t('vault.new.field.full_name')}
                <FieldMarker />
              </label>
              <input
                id={fullNameId}
                className="input"
                type="text"
                value={form.fields.identity.fullName}
                onChange={ev => form.setIdentity('fullName', ev.target.value)}
                autoComplete="name"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={genderId}>
                {t('vault.new.field.gender')}
                <FieldMarker />
              </label>
              <input
                id={genderId}
                className="input"
                type="text"
                value={form.fields.identity.gender}
                onChange={ev => form.setIdentity('gender', ev.target.value)}
                autoComplete="sex"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={dateOfBirthId}>
                {t('vault.new.field.date_of_birth')}
                <FieldMarker />
              </label>
              <input
                id={dateOfBirthId}
                className="input"
                type="text"
                placeholder="YYYY-MM-DD"
                value={form.fields.identity.dateOfBirth}
                onChange={ev => form.setIdentity('dateOfBirth', ev.target.value)}
                autoComplete="bday"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={addressId}>
                {t('vault.new.field.address')}
                <FieldMarker />
              </label>
              <input
                id={addressId}
                className="input"
                type="text"
                value={form.fields.identity.address}
                onChange={ev => form.setIdentity('address', ev.target.value)}
                autoComplete="street-address"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={phoneId}>
                {t('vault.new.field.phone')}
                <FieldMarker />
              </label>
              <input
                id={phoneId}
                className="input"
                type="tel"
                value={form.fields.identity.phone}
                onChange={ev => form.setIdentity('phone', ev.target.value)}
                autoComplete="tel"
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor={emailId}>
                {t('vault.new.field.email')}
                <FieldMarker />
              </label>
              <input
                id={emailId}
                className="input"
                type="email"
                value={form.fields.identity.email}
                onChange={ev => form.setIdentity('email', ev.target.value)}
                autoComplete="email"
              />
            </div>
          </>
        )}

        {folders.length > 0 && (
          <div className="field">
            <label className="field-label" htmlFor={folderSelectId}>
              {t('vault.new.field.folder')}
              <FieldMarker />
            </label>
            <select
              id={folderSelectId}
              className="input"
              value={form.folderId ?? ''}
              onChange={ev => form.setFolderId(ev.target.value || null)}
            >
              <option value="">{t('vault.folder.none')}</option>
              {folders.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* #1: ユーザー定義カスタムフィールド (全 type 共通)。 */}
        <div className="field">
          <label className="field-label">
            {t('vault.new.field.custom_fields')}
            <FieldMarker />
          </label>
          {form.fields.customFields.map((f, i) => (
            <div key={i} className="vault-custom-field-row">
              <select
                className="select vault-custom-field-type"
                value={f.type}
                aria-label={t('vault.new.custom_field.type')}
                onChange={ev =>
                  form.updateCustomField(i, { type: ev.target.value as VaultCustomFieldType })
                }
              >
                <option value="text">{t('vault.new.custom_field.type_text')}</option>
                <option value="hidden">{t('vault.new.custom_field.type_hidden')}</option>
                <option value="boolean">{t('vault.new.custom_field.type_boolean')}</option>
              </select>
              <input
                className="input"
                type="text"
                value={f.name}
                placeholder={t('vault.new.custom_field.name_placeholder')}
                onChange={ev => form.updateCustomField(i, { name: ev.target.value })}
                autoComplete="off"
              />
              {f.type === 'boolean' ? (
                <label className="check-row vault-custom-field-bool">
                  <input
                    type="checkbox"
                    checked={f.value === 'true'}
                    onChange={ev =>
                      form.updateCustomField(i, { value: ev.target.checked ? 'true' : 'false' })
                    }
                  />
                </label>
              ) : (
                <input
                  className="input"
                  type={f.type === 'hidden' ? 'password' : 'text'}
                  value={f.value}
                  onChange={ev => form.updateCustomField(i, { value: ev.target.value })}
                  autoComplete="off"
                />
              )}
              <button
                type="button"
                className="icon-btn"
                onClick={() => form.removeCustomField(i)}
                aria-label={t('vault.new.action.remove_field')}
              >
                <Icon name="trash" aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm vault-list-add-btn"
            onClick={form.addCustomField}
          >
            <Icon name="plus" aria-hidden="true" />
            {t('vault.new.action.add_field')}
          </button>
        </div>

        {/* メモ field は note type 以外でのみ表示する。note type は body (本文)
            が同じ役割を果たすため、UI 重複を避けて非表示にする。 */}
        {form.type !== 'note' && (
          <div className="field">
            <label className="field-label" htmlFor={notesId}>
              {t('vault.new.field.notes')}
              <FieldMarker />
            </label>
            <textarea
              id={notesId}
              className="textarea"
              value={form.fields.common.notes}
              onChange={ev => form.setCommon('notes', ev.target.value)}
              rows={3}
            />
          </div>
        )}

        <div className="vault-new-checks">
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.fields.common.favorite}
              onChange={ev => form.setCommon('favorite', ev.target.checked)}
            />
            {t('vault.new.field.favorite')}
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.lockAfterSave}
              onChange={ev => form.setLockAfterSave(ev.target.checked)}
            />
            {t('vault.new.lock_after_save')}
          </label>
        </div>
      </div>

      {footer !== undefined && <div className="vault-form-actions">{footer}</div>}

      {/* #5: 入力欄の生成アイコンから開く生成ポップアップ。 */}
      {generatorPopup !== null && (
        <GeneratorPopup
          mode={generatorPopup}
          onClose={() => setGeneratorPopup(null)}
          onUse={value => {
            if (generatorPopup === 'username') {
              form.setLogin('username', value)
            } else {
              form.setLogin('password', value)
              setIsPwVisible(true)
            }
          }}
        />
      )}
    </form>
  )
}
